import axios, { AxiosInstance } from 'axios';
import { getVoiceFallbackClient } from './_shared';
import { fetchConversationsPage } from './outboundScheduler';

// ── Missed Call Recovery ───────────────────────────────────────────────────

export type MissedCallStatus = 'pending' | 'scheduled' | 'recovered' | 'failed' | 'opted_out' | 'attempting' | 'expired' | 'blocked';

export type RecoveryMethod = 'auto_callback' | 'sms_followup' | 'manual_callback' | 'voicemail_drop' | 'amd_callback';

export type MissedCallSource = 'inbound' | 'outbound' | 'manual' | 'api' | 'webhook';

export interface MissedCall {
  id: string;
  campaign_id: string;
  campaign_name?: string;
  caller_phone: string;
  called_at: string;
  duration_seconds?: number;
  status: MissedCallStatus;
  callback_scheduled_at?: string;
  attempts: number;
  last_attempt_at?: string;
  source: MissedCallSource;
  attempt_count?: number;
  max_attempts?: number;
  next_attempt_at?: string;
}

export interface MissedCallRecoveryConfig {
  enabled: boolean;
  delay_minutes: number;
  max_attempts: number;
  retry_interval_minutes: number;
  voicemail_message?: string;
  calling_hours?: string;
  recovery_method?: RecoveryMethod;
  first_retry_delay_minutes?: number;
}

export interface MissedCallStats {
  total_missed: number;
  recovery_rate: number;
  callbacks_scheduled: number;
  recovered: number;
  recovery_rate_pct: number;
  pending_count: number;
  by_status?: Record<string, number>;
  by_source?: Record<string, number>;
  by_method?: Record<string, number>;
}

export interface CampaignRecoveryStats {
  campaign_id: string;
  total_missed: number;
  recovered: number;
  recovery_rate_pct: number;
}

export interface SupportedPhoneSystem {
  name: string;
  setup_guide_url?: string;
}

export interface PhoneSystemsPayload {
  webhook_url_template: string;
  supported_systems: SupportedPhoneSystem[];
}

/**
 * `GET /api/recovery/missed-calls` — list missed calls with optional campaign
 * filter and status. Also surfaces the real `total` count the backend
 * computes, for real pagination — same reasoning as fetchConversationsPage.
 */
export async function fetchMissedCalls(
  params?: { campaign_id?: string; status?: MissedCallStatus; days?: number; limit?: number; offset?: number },
  axiosInstance?: AxiosInstance
): Promise<{ missed_calls: MissedCall[]; total: number }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`/api/recovery/missed-calls`, { params });
  const rd = response.data;
  const missed_calls: MissedCall[] = Array.isArray(rd) ? rd : rd?.missed_calls ?? [];
  const total = Array.isArray(rd) ? missed_calls.length : Number(rd?.total ?? missed_calls.length);
  return { missed_calls, total };
}

/** `GET /api/recovery/stats` — aggregate recovery statistics. */
export async function fetchMissedCallStats(
  params?: { campaign_id?: string; days?: number },
  axiosInstance?: AxiosInstance
): Promise<MissedCallStats> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`/api/recovery/stats`, { params });
  const d = response.data ?? {};
  return {
    total_missed: d.total_missed ?? 0,
    recovery_rate: d.recovery_rate ?? 0,
    callbacks_scheduled: d.callbacks_scheduled ?? 0,
    recovered: d.recovered ?? 0,
    recovery_rate_pct: d.recovery_rate_pct ?? 0,
    pending_count: d.pending_count ?? 0,
    by_status: d.by_status ?? {},
    by_source: d.by_source ?? {},
    by_method: d.by_method ?? {},
  };
}

/** `POST /api/recovery/missed-calls/{id}/retry` — retry recovery for a missed call. */
export async function retryMissedCall(
  missedCallId: string,
  recoveryMethod?: RecoveryMethod,
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean; scheduled_at?: string }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(
    `/api/recovery/missed-calls/${missedCallId}/retry`,
    { recovery_method: recoveryMethod }
  );
  return { ok: response.status === 200 || response.data?.ok || true, scheduled_at: response.data?.scheduled_at };
}

/** @deprecated Use retryMissedCall instead */
export async function scheduleMissedCallCallback(
  missedCallId: string,
  delay_minutes?: number,
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean; scheduled_at: string }> {
  const res = await retryMissedCall(missedCallId, 'auto_callback', axiosInstance);
  return { ok: res.ok, scheduled_at: res.scheduled_at ?? '' };
}

/** `GET /api/recovery/config/{id}` — get missed call recovery config for a campaign. */
export async function fetchCampaignRecoveryConfig(
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<MissedCallRecoveryConfig> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`/api/recovery/config/${campaignId}`);
  return response.data ?? { enabled: false, delay_minutes: 5, max_attempts: 3, retry_interval_minutes: 60 };
}

/** `PUT /api/recovery/config/{id}` — save missed call recovery config. */
export async function saveCampaignRecoveryConfig(
  campaignId: string,
  config: MissedCallRecoveryConfig,
  axiosInstance?: AxiosInstance
): Promise<MissedCallRecoveryConfig> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.put<any>(`/api/recovery/config/${campaignId}`, config);
  return response.data ?? config;
}

/** `GET /api/recovery/missed-calls/{id}` — fetch detail of a single missed call. */
export async function fetchMissedCall(
  missedCallId: string,
  axiosInstance?: AxiosInstance
): Promise<MissedCall> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<MissedCall>(`/api/recovery/missed-calls/${missedCallId}`);
  return response.data;
}

/** `POST /api/recovery/missed-calls` — create a manual missed call. */
export async function createManualMissedCall(
  payload: { caller_phone: string; campaign_id: string; reason?: string; recovery_method?: RecoveryMethod },
  axiosInstance?: AxiosInstance
): Promise<MissedCall> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<MissedCall>(`/api/recovery/missed-calls`, payload);
  return response.data;
}

/** `POST /api/recovery/missed-calls/{id}/cancel` — cancel recovery for a missed call. */
export async function cancelMissedCall(
  missedCallId: string,
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`/api/recovery/missed-calls/${missedCallId}/cancel`);
  return { ok: response.status === 200 || response.data?.ok || true };
}

/** `GET /api/recovery/config/{campaignId}/stats` — fetch recovery stats for a campaign. */
export async function fetchCampaignRecoveryStats(
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<CampaignRecoveryStats> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<CampaignRecoveryStats>(`/api/recovery/config/${campaignId}/stats`);
  return response.data;
}

/** `GET /api/recovery/phone-systems` — fetch supported phone systems and guide. */
export async function fetchSupportedPhoneSystems(
  axiosInstance?: AxiosInstance
): Promise<PhoneSystemsPayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<PhoneSystemsPayload>(`/api/recovery/phone-systems`);
  return response.data;
}
